/**
 * Favorites Agent: Custom hooks for favorites and comparison management.
 * Handles favorite properties and property comparisons.
 */

import { useState, useCallback, useMemo } from 'react';
import APIAgent from './apiAgent';
import asArray from '../utils/asArray';

/**
 * Hook for managing favorite properties
 */
export const useFavorites = (buyerId) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch saved favorites
   */
  const fetchFavorites = useCallback(async () => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/favorites`,
        'GET'
      );
      setFavorites(asArray(data));
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Add property to favorites
   */
  const addFavorite = useCallback(async (houseId, notes = '') => {
    try {
      setError(null);
      const data = await APIAgent.request('/api/favorites', 'POST', {
        buyer_id: buyerId,
        house_id: houseId,
        notes,
      });
      setFavorites((prev) => {
        if (prev.some((fav) => fav.house_id === data.house_id)) {
          return prev;
        }
        return [...prev, data];
      });
      return data;
    } catch (err) {
      console.error('Error adding favorite:', err);
      setError(err.message);
      throw err;
    }
  }, [buyerId]);

  /**
   * Remove property from favorites
   */
  const removeFavorite = useCallback(async (houseId) => {
    try {
      setError(null);
      await APIAgent.request(
        `/api/favorites/house/${houseId}?buyer_id=${buyerId}`,
        'DELETE'
      );
      setFavorites((prev) =>
        prev.filter((fav) => fav.house_id !== houseId)
      );
    } catch (err) {
      console.error('Error removing favorite:', err);
      setError(err.message);
      throw err;
    }
  }, [buyerId]);

  const favoriteHouseIds = useMemo(
    () => new Set(favorites.map((fav) => fav.house_id)),
    [favorites]
  );

  /**
   * Check if property is favorited
   */
  const isFavorite = useCallback((houseId) => {
    return favoriteHouseIds.has(houseId);
  }, [favoriteHouseIds]);

  /**
   * Check favorite status on backend
   */
  const checkFavoriteStatus = useCallback(async (houseId) => {
    try {
      const data = await APIAgent.request(
        `/api/favorites/check/${buyerId}/${houseId}`,
        'GET'
      );
      return data.is_favorite;
    } catch (err) {
      console.error('Error checking favorite status:', err);
      return false;
    }
  }, [buyerId]);

  return {
    favorites,
    favoriteHouseIds,
    loading,
    error,
    fetchFavorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    checkFavoriteStatus,
  };
};

/**
 * Hook for managing comparison lists
 */
export const useComparisons = (buyerId) => {
  const [comparisons, setComparisons] = useState([]);
  const [currentComparison, setCurrentComparison] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all comparison lists
   */
  const fetchComparisons = useCallback(async () => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/comparisons`,
        'GET'
      );
      setComparisons(asArray(data));
    } catch (err) {
      console.error('Error fetching comparisons:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Create a new comparison list
   */
  const createComparison = useCallback(async (name, description = '') => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request('/api/comparisons', 'POST', {
        buyer_id: buyerId,
        name,
        description,
      });
      setComparisons((prev) => [...prev, data]);
      setCurrentComparison(data);
      return data;
    } catch (err) {
      console.error('Error creating comparison:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Get comparison details
   */
  const getComparison = useCallback(async (comparisonId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/comparisons/${comparisonId}`,
        'GET'
      );
      setCurrentComparison(data);
      return data;
    } catch (err) {
      console.error('Error getting comparison:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update comparison list
   */
  const updateComparison = useCallback(async (comparisonId, updates) => {
    try {
      setError(null);
      const data = await APIAgent.request(
        `/api/comparisons/${comparisonId}`,
        'PUT',
        updates
      );
      setCurrentComparison(data);
      setComparisons((prev) =>
        prev.map((c) => (c.id === comparisonId ? data : c))
      );
      return data;
    } catch (err) {
      console.error('Error updating comparison:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete comparison list
   */
  const deleteComparison = useCallback(async (comparisonId) => {
    try {
      setError(null);
      await APIAgent.request(`/api/comparisons/${comparisonId}`, 'DELETE');
      setComparisons((prev) =>
        prev.filter((c) => c.id !== comparisonId)
      );
      if (currentComparison?.id === comparisonId) {
        setCurrentComparison(null);
      }
    } catch (err) {
      console.error('Error deleting comparison:', err);
      setError(err.message);
      throw err;
    }
  }, [currentComparison?.id]);

  /**
   * Add property to comparison
   */
  const addItemToComparison = useCallback(async (comparisonId, houseId) => {
    try {
      setError(null);
      const data = await APIAgent.request(
        `/api/comparisons/${comparisonId}/items`,
        'POST',
        { house_id: houseId }
      );
      return data;
    } catch (err) {
      console.error('Error adding item to comparison:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Remove property from comparison
   */
  const removeItemFromComparison = useCallback(
    async (comparisonId, houseId) => {
      try {
        setError(null);
        await APIAgent.request(
          `/api/comparisons/${comparisonId}/items/${houseId}`,
          'DELETE'
        );
      } catch (err) {
        console.error('Error removing item from comparison:', err);
        setError(err.message);
        throw err;
      }
    },
    []
  );

  /**
   * Get comparison analysis
   */
  const getComparisonAnalysis = useCallback(async (comparisonId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/comparisons/${comparisonId}/analysis`,
        'GET'
      );
      setAnalysis(data);
      return data;
    } catch (err) {
      console.error('Error getting comparison analysis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    comparisons,
    currentComparison,
    analysis,
    loading,
    error,
    fetchComparisons,
    createComparison,
    getComparison,
    updateComparison,
    deleteComparison,
    addItemToComparison,
    removeItemFromComparison,
    getComparisonAnalysis,
  };
};
